/**
 * A kind of file stored in the code components bucket (library publish and
 * site module bundling share this layout).
 *
 * An artifact names an S3 lane and the rules that lane is stored and served
 * under. Disk layout for that lane is `outDir` (e.g. `dist/Client`). That is
 * separate from build `Environment` in code-libraries, which is still the
 * bundler Client/Server target axis — not the storage key.
 */
export enum CodeModuleArtifact {
  ComponentsBundle = 'ComponentsBundle',
  ComponentsSSRBundle = 'ComponentsSSRBundle',
  FunctionsBundle = 'FunctionsBundle',
}

export type CodeModuleArtifactConfig = {
  /** Directory under `{resourceId}/` in the code components bucket. */
  dir: string;
  /**
   * Public artifacts get a CloudFlare CDN url minted for them and rely on the
   * bucket policy granting s3:GetObject on their prefix. Private artifacts must
   * never appear in a public url map. Object ACLs are never used; private
   * prefixes are locked down via bucket policy (EXT-1615).
   */
  visibility: 'public' | 'private';
  /**
   * Well-known file at the root of the artifact's prefix. Consumers address the
   * artifact by this name, so it is the lane's entry point, not a bundler detail.
   */
  entryFile: string;
  /**
   * Relative directory under the library working root where the bundler writes
   * this artifact (today: `dist/Client`, `dist/Server`).
   */
  outDir: string;
};

export const CODE_MODULE_ARTIFACTS = {
  [CodeModuleArtifact.ComponentsBundle]: {
    dir: 'module',
    visibility: 'public',
    entryFile: 'wf-manifest.json',
    outDir: 'dist/Client',
  },
  [CodeModuleArtifact.ComponentsSSRBundle]: {
    dir: 'ssr-module',
    visibility: 'public',
    entryFile: 'ssr-bundle.tar',
    outDir: 'dist/Server',
  },
  [CodeModuleArtifact.FunctionsBundle]: {
    dir: 'functions-module',
    visibility: 'private',
    entryFile: 'bundle.tar',
    outDir: 'dist/Functions',
  },
} as const satisfies Record<CodeModuleArtifact, CodeModuleArtifactConfig>;

/**
 * Artifacts that may be handed to a client as a public url. Derived from the
 * table so a private artifact is a compile error in a public url map rather
 * than something a reader has to remember to exclude.
 */
export type PublicCodeModuleArtifact = {
  [K in CodeModuleArtifact]: (typeof CODE_MODULE_ARTIFACTS)[K]['visibility'] extends 'public'
    ? K
    : never;
}[CodeModuleArtifact];

export const PUBLIC_CODE_MODULE_ARTIFACTS = Object.values(
  CodeModuleArtifact
).filter(
  (artifact) => CODE_MODULE_ARTIFACTS[artifact].visibility === 'public'
) as PublicCodeModuleArtifact[];

export type PrivateCodeModuleArtifact = {
  [K in CodeModuleArtifact]: (typeof CODE_MODULE_ARTIFACTS)[K]['visibility'] extends 'private'
    ? K
    : never;
}[CodeModuleArtifact];

export const PRIVATE_CODE_MODULE_ARTIFACTS = Object.values(
  CodeModuleArtifact
).filter(
  (artifact) => CODE_MODULE_ARTIFACTS[artifact].visibility === 'private'
) as PrivateCodeModuleArtifact[];

export type PrivateCodeModuleArtifactKeys = Record<
  PrivateCodeModuleArtifact,
  {s3Key: string}
>;
